import { Helmet } from "react-helmet-async";

/**
 * SEO helmet wrapper — accepts title, description, canonical, image, and json_ld[].
 *
 * R110.25 — Rewritten for GSC-compliant self-canonical + hreflang trio.
 *
 * Two ways to use:
 *
 *   1) NEW (preferred) — pass `path` (path-only, no query):
 *        <SEOHead path="/blog/idli-batter-business" ... />
 *      SEOHead itself computes:
 *        canonical = `${SITE}${path}` when current URL has no ?lang=hi
 *        canonical = `${SITE}${path}?lang=hi` when current URL has ?lang=hi
 *        hreflang:  en  = `${SITE}${path}`
 *                   hi  = `${SITE}${path}?lang=hi`
 *                   x-default = `${SITE}${path}`
 *
 *   2) OLD (legacy) — pass explicit `canonical` (+ optional `hreflangs`).
 *      SEOHead falls back to `window.location.href` if neither is given.
 *
 * The `robots` prop lets callers force `noindex, nofollow` on:
 *   - not-found states (soft-404 prevention)
 *   - auth / dashboard / admin pages
 *   - draft or short-form blog posts
 */
const SITE_ORIGIN = "https://venturekhoj.com";

function _stripLangQuery(url) {
  if (!url) return url;
  // Removes both `?lang=xx` (leading) and `&lang=xx` (trailing) forms.
  return url.replace(/([?&])lang=(en|hi)&?/g, (_, p1, _p2) => (p1 === "?" ? "?" : "&"))
            .replace(/[?&]$/, "");
}

export function SEOHead({
  title,
  description,
  canonical,
  path,
  image,
  jsonLd = [],
  lang,
  hreflangs,
  robots = "index, follow",
}) {
  const t = title || "VentureKhoj™ | Discover. Compare. Decide.";
  const d =
    description ||
    "Discover and compare business ideas matched to your budget, experience, location and goals.";
  const img = image || `${SITE_ORIGIN}/brand/vk-social-og.png?v=2`;

  // ---- Language detection: prop wins, else URL, else "en" ---------------
  let currentLang = lang;
  if (!currentLang && typeof window !== "undefined") {
    const params = new URLSearchParams(window.location.search);
    currentLang = params.get("lang") === "hi" ? "hi" : "en";
  }
  currentLang = currentLang === "hi" ? "hi" : "en";
  const ogLocale = currentLang === "hi" ? "hi_IN" : "en_IN";

  // ---- Self-canonical + hreflang trio -----------------------------------
  let self, enUrl, hiUrl;
  if (path) {
    // Path-based (new preferred API) — deterministic, no window.location drift.
    const clean = path.startsWith("/") ? path : `/${path}`;
    enUrl = `${SITE_ORIGIN}${clean}`;
    hiUrl = `${SITE_ORIGIN}${clean}?lang=hi`;
    self  = currentLang === "hi" ? hiUrl : enUrl;
  } else if (canonical) {
    // Legacy API — treat provided canonical as EN base.
    const base = _stripLangQuery(canonical);
    enUrl = base;
    hiUrl = base.includes("?") ? `${base}&lang=hi` : `${base}?lang=hi`;
    self  = currentLang === "hi" ? hiUrl : enUrl;
  } else if (typeof window !== "undefined") {
    const base = _stripLangQuery(window.location.origin + window.location.pathname);
    enUrl = base;
    hiUrl = `${base}?lang=hi`;
    self  = currentLang === "hi" ? hiUrl : enUrl;
  }

  // Callers may override — but the default trio covers every case correctly.
  const alts = hreflangs || (
    enUrl && hiUrl
      ? [
          { hreflang: "en",        href: enUrl },
          { hreflang: "hi",        href: hiUrl },
          { hreflang: "x-default", href: enUrl },
        ]
      : []
  );

  return (
    <Helmet>
      <html lang={currentLang} />
      <title>{t}</title>
      <meta name="description" content={d} />
      {self && <link rel="canonical" href={self} />}
      <meta name="robots" content={robots} />

      {alts.map((a) => (
        <link key={a.hreflang} rel="alternate" hrefLang={a.hreflang} href={a.href} />
      ))}

      <meta property="og:type" content="website" />
      <meta property="og:title" content={t} />
      <meta property="og:description" content={d} />
      {self && <meta property="og:url" content={self} />}
      <meta property="og:image" content={img} />
      <meta property="og:image:width"  content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt"    content={t} />
      <meta property="og:site_name" content="VentureKhoj™" />
      <meta property="og:locale" content={ogLocale} />
      {currentLang === "hi" && <meta property="og:locale:alternate" content="en_IN" />}
      {currentLang === "en" && <meta property="og:locale:alternate" content="hi_IN" />}

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={t} />
      <meta name="twitter:description" content={d} />
      <meta name="twitter:image" content={img} />
      <meta name="twitter:image:alt" content={t} />

      {jsonLd.map((obj, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(obj)}
        </script>
      ))}
    </Helmet>
  );
}
